import { useTransmitChannel } from "@/hooks/use-transmit-channel";
import { shouldShowInternalToast } from "@/lib/internal-message-toast";
import { normalizeNotificationSoundSettings } from "@/lib/notification-sounds";
import type { SharedData } from "@/types";
import { router, usePage } from "@inertiajs/react";
import { MessageSquare, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { urlFor } from "~/client";

type ActiveConversation = { id: number | string | bigint } | null | undefined;

type ListUpdatePayload = Parameters<typeof shouldShowInternalToast>[0];

type ListUpdatePageProps = SharedData & {
  activeConversation?: ActiveConversation;
};

export default function InternalMessageToastListener() {
  const { t } = useTranslation("chat");
  const { props } = usePage<ListUpdatePageProps>();
  const auth = props.auth;
  const companyId = auth?.company?.id;
  const currentUserId = auth?.user?.id ?? null;
  const activeConversationId = props.activeConversation?.id ?? null;
  const notificationSoundSettings = auth?.notificationSoundSettings;
  const seenRef = useRef<Set<string>>(new Set());

  // Track the ids of toasts currently on screen so we can offer a single
  // "dismiss all" affordance once more than one balloon is stacked.
  const activeIdsRef = useRef<Set<string>>(new Set());
  const [activeCount, setActiveCount] = useState(0);

  const removeActive = useCallback((id: string) => {
    if (activeIdsRef.current.delete(id)) {
      setActiveCount(activeIdsRef.current.size);
    }
  }, []);

  const dismissAll = useCallback(() => {
    for (const id of activeIdsRef.current) {
      toast.dismiss(id);
    }
    activeIdsRef.current.clear();
    setActiveCount(0);
  }, []);

  // Keep the latest values in refs so `handleMessage` stays referentially stable
  // across navigation; otherwise `useTransmitChannel` would resubscribe the SSE
  // channel on every conversation change.
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;
  const notificationSoundSettingsRef = useRef(notificationSoundSettings);
  notificationSoundSettingsRef.current = notificationSoundSettings;

  const topic =
    companyId && currentUserId
      ? `/${companyId}/user/${currentUserId}/conversations/list-updates`
      : null;

  const handleMessage = useCallback(
    (data: ListUpdatePayload) => {
      if (!data) {
        return;
      }

      // The visual balloon is suppressed only when the user has specifically
      // muted the "internal" category — not when notification sound is globally
      // off (sound and visual toast are independent modalities).
      const internalMuted = normalizeNotificationSoundSettings(
        notificationSoundSettingsRef.current,
      ).mutedCategories.includes("internal");

      const decision = shouldShowInternalToast(data, {
        currentUserId: currentUserIdRef.current,
        activeConversationId: activeConversationIdRef.current,
        internalMuted,
      });

      if (!decision.show) {
        return;
      }

      if (seenRef.current.has(decision.dedupeKey)) {
        return;
      }
      seenRef.current.add(decision.dedupeKey);
      if (seenRef.current.size > 200) {
        const oldest = seenRef.current.values().next().value;
        if (oldest !== undefined) {
          seenRef.current.delete(oldest);
        }
      }

      const title = decision.title || t("internalMessageToast.fallbackTitle");
      const body = decision.body || t("internalMessageToast.emptyBody");
      const conversationUrl = urlFor("channels.show", {
        conversationId: decision.conversationId,
      });

      const toastId = decision.dedupeKey;
      activeIdsRef.current.add(toastId);
      setActiveCount(activeIdsRef.current.size);

      toast.custom(
        () => (
          <div className="border-border bg-popover ring-foreground/10 relative flex w-[min(360px,calc(100vw-2rem))] items-start gap-3 rounded-lg border p-3 shadow-2xl ring-1">
            <button
              type="button"
              aria-label={title}
              onClick={() => {
                router.visit(conversationUrl);
                toast.dismiss(toastId);
                removeActive(toastId);
              }}
              className="hover:bg-accent -m-1 flex flex-1 items-start gap-3 rounded-md p-1 text-left transition-colors"
            >
              <div className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
                <MessageSquare className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate pr-5 text-sm font-semibold">{title}</p>
                <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs break-words">
                  {body}
                </p>
              </div>
            </button>
            <button
              type="button"
              aria-label={t("internalMessageToast.dismiss")}
              onClick={() => {
                toast.dismiss(toastId);
                removeActive(toastId);
              }}
              className="text-muted-foreground hover:text-foreground hover:bg-accent absolute top-1.5 right-1.5 rounded-md p-1 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
        {
          id: toastId,
          duration: 6000,
          unstyled: true,
          onDismiss: () => removeActive(toastId),
          onAutoClose: () => removeActive(toastId),
        },
      );
    },
    [t, removeActive],
  );

  useTransmitChannel<ListUpdatePayload>(topic, handleMessage);

  if (activeCount < 2) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={dismissAll}
      className="bg-foreground text-background hover:bg-foreground/90 fixed right-4 bottom-4 z-[999999999] rounded-full px-3 py-1.5 text-xs font-medium shadow-lg transition-colors"
    >
      {t("internalMessageToast.dismissAll")} ({activeCount})
    </button>
  );
}
